export default {
  id: 'reading-scanner-output',
  title: 'Reading Scanner Output',
  content: `## Reading Scanner Output

The scanner feed shows you stocks that match technical conditions right now. Here is how to read each field and how to decide what to do with a hit.

---

## The Scanner Card

Each scanner result appears as a card in the Analytics tab. Here is what every field means:

| Field | How to read it |
|---|---|
| **Symbol** | NSE ticker. Tap to open the stock's chart |
| **Signal name** | The scanner that fired — tells you what type of setup this is |
| **Current price** | Last fetched price — updates periodically, not tick-by-tick |
| **Volume ratio** | Current session volume ÷ 20-day average volume |
| **RSI** | Current RSI(14) — momentum reading |
| **Trend** | UPTREND / DOWNTREND / SIDEWAYS vs SMA50 |
| **Signal strength** | A 0–100 composite of how cleanly this instance matches ideal conditions |
| **Time** | When this scanner result was last updated |

---

## Reading Volume Ratio

Volume ratio tells you how much activity there is behind a move compared to normal.

| Ratio | What it means |
|---|---|
| < 0.8× | Below average — limited participation |
| 0.8–1.5× | Normal range |
| 1.5–2.5× | Above average — meaningful activity |
| > 2.5× | High conviction — institutional-level participation |

> A breakout on below-average volume almost always fails. Volume is the fuel — price is the car.

---

## Reading RSI in Context

RSI alone does not tell you what to do. It tells you momentum context.

| RSI level | Context |
|---|---|
| < 30 | Oversold — potential exhaustion of selling. Watch for reversal signals |
| 30–50 | Bearish momentum territory |
| 50–70 | Bullish momentum territory |
| > 70 | Overbought — strong upward momentum, potential extension or exhaustion |

For a breakout scanner: RSI at 55 (supportive) is better than RSI at 78 (extended). For a reversal scanner: RSI at 28 (oversold) is more interesting than RSI at 45.

---

## Reading Trend Direction

Trend shows whether the stock is above or below its SMA50.

| Trend | What it means |
|---|---|
| UPTREND | Price above SMA50 — medium-term buyers in control |
| DOWNTREND | Price below SMA50 — medium-term sellers in control |
| SIDEWAYS | Price oscillating near SMA50 — no clear trend |

Bullish scanner signals (breakouts, momentum) score higher when trend is UPTREND. Entering a bullish breakout in a DOWNTREND adds significant counter-trend risk.

---

## Signal Strength Score

The signal strength is a 0–100 composite calculated from:
- How cleanly the condition threshold was crossed
- Volume confirmation
- Trend alignment
- RSI context

A signal strength of 80+ means all supporting factors are well-aligned. A signal of 40 means the trigger fired but the supporting context is weak.

---

## Step-by-Step: Evaluating a Scanner Hit

**1. Check the signal name** — understand what type of setup it is (breakout, reversal, momentum)

**2. Check trend direction** — is the signal aligned with the medium-term trend?

**3. Check volume ratio** — is there conviction behind the move?

**4. Check RSI** — is momentum in a supportive range for this signal type?

**5. Tap the symbol to open the chart** — does the signal make visual sense on the chart?

**6. Check signal strength** — is it 70+? Below 50 with a weak context, pass.

**7. Open Nyx Analysis Mode** — see the AI explanation for this stock today

**8. If everything aligns** — plan the trade in ACT or place it on the Trade page

---

## Using Filters Effectively

Start broad, then narrow:
- Set a volume ratio minimum of **1.3×** to filter out low-conviction signals
- Filter by **UPTREND** when looking for long setups
- Use the **RSI range** filter if you are specifically looking for oversold bounces or momentum entries
- Filter by **sector** if you are focusing on a specific area of the market`,
};
